import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { emptyFiltersMsg, filterMsg, searchMsg, t } from '../../../common/i18n/index.js';
import {
  clearFilterSelections,
  getSelectedFilterCount,
  hasActiveFilters,
} from '../../../common/utils/productListUtilsCoCo.js';
import { ProductGridFilterCoCo } from '../../ProductGridCoCo/ProductGridFilterCoCo.js';
import { ProductGridFilterTagsCoCo } from '../../ProductGridCoCo/ProductGridFilterTagsCoCo.js';
import { ProductGridSearchCoCo } from '../../ProductGridCoCo/ProductGridSearchCoCo.js';
import { ToggleButton } from '../../ToggleButton/ToggleButton.js';
import { DeviceSelectionSortCoCo } from './DeviceSelectionSortCoCo.js';
import type { DeviceSelectionFiltersCoCo } from '../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { SortType } from '../../ProductGridCoCo/ProductGridSortCoCo.js';

import '../../ProductGrid/ProductGrid.scss';
import './DeviceSelectionDevicesToolbarCoCo.scss';

interface DeviceSelectionDevicesToolbarCoCoProps {
  filters: DeviceSelectionFiltersCoCo;
  search: string;
  sort: SortType;
  onFiltersChange: (filters: DeviceSelectionFiltersCoCo) => void;
  onSearchChange: (search: string) => void;
  onSortChange: (sort: SortType) => void;
  onShowSelectedFirst?: () => void;
  defaultFiltersExpanded?: boolean;
}

export const DeviceSelectionDevicesToolbarCoCo = ({
  filters,
  search,
  sort,
  onFiltersChange,
  onSearchChange,
  onSortChange,
  onShowSelectedFirst,
  defaultFiltersExpanded = false,
}: DeviceSelectionDevicesToolbarCoCoProps) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(defaultFiltersExpanded);
  const filterToggleContainerRef = useRef<HTMLSpanElement>(null);
  const closeFilters = () => {
    setIsFilterExpanded(false);
    filterToggleContainerRef.current?.querySelector('button')?.focus();
  };

  useEffect(() => {
    if (!isFilterExpanded) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsFilterExpanded(false);
        filterToggleContainerRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFilterExpanded]);

  return (
    <div className="of-device-selection-devices-toolbar-coco">
      <div className="of-device-selection-devices-toolbar-coco__controls">
        <div className="of-device-selection-devices-toolbar-coco__search">
          <ProductGridSearchCoCo placeholder={t.VQE6(searchMsg)} value={search} onChange={onSearchChange} />
        </div>
        {hasActiveFilters(filters) && (
          <span ref={filterToggleContainerRef}>
            <ToggleButton
              className="of-device-selection-devices-toolbar-coco__button"
              buttonLabel={t.XUFV(filterMsg)}
              isOpen={isFilterExpanded}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            />
          </span>
        )}
        <DeviceSelectionSortCoCo selectedValue={sort} onChange={onSortChange} />
        {onShowSelectedFirst && (
          <CL.Button
            type="button"
            color="link"
            size="l"
            className="of-device-selection-devices-toolbar-coco__button of-device-selection-devices-toolbar-coco__selected-first"
            onClick={onShowSelectedFirst}
          >
            <span>{t.P9S4('Show selected first')}</span>
            <CL.Icon icon="update" aria-hidden="true" />
          </CL.Button>
        )}
      </div>
      {getSelectedFilterCount(filters) > 0 && (
        <div className="of-device-selection-devices-toolbar-coco__selected-filters">
          <ProductGridFilterTagsCoCo filters={filters} setFilters={onFiltersChange} />
          <CL.Button
            type="button"
            color="link"
            size="l"
            className="of-device-selection-devices-toolbar-coco__button"
            onClick={() => onFiltersChange(clearFilterSelections(filters))}
          >
            {t.IWVK(emptyFiltersMsg)}
          </CL.Button>
        </div>
      )}
      {isFilterExpanded && (
        <div className="of-device-selection-devices-toolbar-coco__filters of-product-grid__filter-content">
          <ProductGridFilterCoCo filters={filters} onChange={onFiltersChange} onClose={closeFilters} />
        </div>
      )}
    </div>
  );
};
